/* eslint-disable no-mixed-spaces-and-tabs */
import { call, fork, put, takeEvery, select } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { stopSubmit, touch } from 'redux-form';
import { notificationInit } from '../../../../modules/notifications/actions';
import { FORM_NAME, FORM_FIELDS, REQUEST_ERROR_MESSAGE } from './constants';
import { DISMISS_TIME } from '../../../../api/constants';
import { errorHandler } from '../../../../api/utils';
import { GROUP_TO_WAY, WAY_TO_WAY, ORDER_GROUP_TO_WAY, ORDER_WAY_TO_WAY } from './actions';
import {
	getGroupsListAction,
	getCollectionGroupListAction,
	getCollectionWayListAction,
	getWaysGroupListAction,
	setIsLoadingAction,
	getOrdersCollectionGroupListAction,
	getOrdersCollectionWayListAction,
	getGroupsWithWaysListAction,
} from '../../actions';
import {
	getCollectionGroupOpenSelector,
	getCollectionWayOpenSelector,
	getGroupOpenSelector,
	getGroupWayOpenSelector,
	getWayOpenSelector,
} from '../../selectors';
import {
	groupToWayRequest,
	orderGroupToWayRequest,
	orderWayToWayRequest,
	wayToWayRequest,
} from '../../../../api/requests';
import { getFormIsInvalidWaySelector } from './selectors';
import { transformToValidationErrors } from './utils';

export function* groupToWaySaga({ payload: { searchQuery, group, way_id, callback } }) {
	try {
		const groupOpen = yield select(getGroupOpenSelector());

		const groupWayOpen = yield select(getGroupWayOpenSelector());

		const wayOpen = yield select(getWayOpenSelector());

		const isInvalid = yield select(getFormIsInvalidWaySelector());

		if (isInvalid) return;

		const { data, message, toast, errors } = yield call(groupToWayRequest, group.id, group.process?.id, {
			way_id,
		});

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(getGroupsListAction({ searchQuery, noLoading: true }));
			yield put(getGroupsWithWaysListAction({ searchQuery, noLoading: true }));

			if (groupOpen && groupOpen.id === group.id) {
				yield put(getCollectionGroupListAction({ row: groupOpen, searchQuery }));
			}

			if (wayOpen && wayOpen.id === way_id) {
				yield put(getCollectionWayListAction({ row: wayOpen, searchQuery }));
			}

			if (groupWayOpen) {
				yield put(getWaysGroupListAction({ row: groupWayOpen, searchQuery, noLoading: true }));
			}

			callback();
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...FORM_FIELDS));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	}
}

export function* wayToWaySaga({ payload: { searchQuery, way, to_id, callback } }) {
	try {
		const groupWayOpen = yield select(getGroupWayOpenSelector());

		const wayOpen = yield select(getWayOpenSelector());

		const isInvalid = yield select(getFormIsInvalidWaySelector());

		if (isInvalid) return;

		const { data, message, toast, errors } = yield call(wayToWayRequest, way.id, { way_id: to_id });

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(getGroupsWithWaysListAction({ searchQuery, noLoading: true }));

			if (wayOpen && (wayOpen.id === way.id || wayOpen.id === to_id) && !wayOpen?.planning) {
				yield put(getCollectionWayListAction({ row: wayOpen, searchQuery, noLoading: true }));
			}

			if (groupWayOpen) {
				yield put(getWaysGroupListAction({ row: groupWayOpen, searchQuery, noLoading: true }));
			}

			callback();
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...FORM_FIELDS));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	}
}

export function* orderGroupToWaySaga({ payload: { searchQuery, collection, orders, group, way_id, callback } }) {
	try {
		yield put(setIsLoadingAction(true));

		const groupOpen = yield select(getGroupOpenSelector());

		const groupWayOpen = yield select(getGroupWayOpenSelector());

		const wayOpen = yield select(getWayOpenSelector());

		const collectionGroupOpen = yield select(getCollectionGroupOpenSelector());

		const collectionWayOpen = yield select(getCollectionWayOpenSelector());

		const isInvalid = yield select(getFormIsInvalidWaySelector());

		if (isInvalid) return;

		const { data, message, toast, errors } = collection
			? yield call(orderGroupToWayRequest, { collection, group_id: group.id, way_id })
			: yield call(orderGroupToWayRequest, { orders, group_id: group.id, way_id });

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(getGroupsListAction({ searchQuery, noLoading: true }));
			yield put(getGroupsWithWaysListAction({ searchQuery, noLoading: true }));

			if (groupOpen && groupOpen.id === group.id) {
				yield put(getCollectionGroupListAction({ row: groupOpen, searchQuery }));

				if (collectionGroupOpen) {
					yield put(
						getOrdersCollectionGroupListAction({
							row: groupOpen,
							collection: collectionGroupOpen,
							searchQuery,
						}),
					);
				}
			}

			if (wayOpen && wayOpen.id === way_id) {
				yield put(getCollectionWayListAction({ row: wayOpen, searchQuery }));

				if (collectionWayOpen) {
					yield put(
						getOrdersCollectionWayListAction({
							row: wayOpen,
							collection: collectionWayOpen,
							searchQuery,
						}),
					);
				}
			}

			if (groupWayOpen) {
				yield put(getWaysGroupListAction({ row: groupWayOpen, searchQuery, noLoading: true }));
			}

			callback();
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...FORM_FIELDS));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsLoadingAction(false));
	}
}

export function* orderWayToWaySaga({ payload: { searchQuery, collection, orders, way, to_id, callback } }) {
	try {
		yield put(setIsLoadingAction(true));

		const groupWayOpen = yield select(getGroupWayOpenSelector());

		const wayOpen = yield select(getWayOpenSelector());

		const collectionWayOpen = yield select(getCollectionWayOpenSelector());

		const isInvalid = yield select(getFormIsInvalidWaySelector());

		if (isInvalid) return;

		const { data, message, toast, errors } = collection
			? yield call(orderWayToWayRequest, { collection, from_id: way.id, to_id })
			: yield call(orderWayToWayRequest, { orders, from_id: way.id, to_id });

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			if (wayOpen && (wayOpen.id === way.id || wayOpen.id === to_id)) {
				yield put(getCollectionWayListAction({ row: wayOpen, searchQuery }));

				if (collectionWayOpen) {
					yield put(
						getOrdersCollectionWayListAction({ row: wayOpen, collection: collectionWayOpen, searchQuery }),
					);
				}
			}

			if (groupWayOpen) {
				yield put(getWaysGroupListAction({ row: groupWayOpen, searchQuery, noLoading: true }));
			}

			callback();
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...FORM_FIELDS));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsLoadingAction(false));
	}
}

export default function* orderGroupsChangeWayContainerSaga() {
	yield takeEvery(GROUP_TO_WAY, groupToWaySaga);
	yield takeEvery(WAY_TO_WAY, wayToWaySaga);
	yield takeEvery(ORDER_GROUP_TO_WAY, orderGroupToWaySaga);
	yield takeEvery(ORDER_WAY_TO_WAY, orderWayToWaySaga);
}
